import { WatchInterface } from "./watchInterface";
import { Observable, Observer } from "rxjs";
import { deleteNullProperties } from "../../utils/arrayUtils";
import { copyObject } from "refsix-core";
import * as Sentry from "@sentry/react";
import {
    GarminDeviceStatusChangeEvent,
    GarminSDK,
    IQDevice,
} from "capacitor-garmin-sdk";
import { store } from "../../redux/store";
import { GarminWatches } from "../../redux/models/refsixState";
import { PluginListenerHandle } from "@capacitor/core/types/definitions";
import { WatchType } from "./watchService";
import { Capacitor } from "@capacitor/core";
import { GARMIN_FULL_APP_INFO } from "../../constants/GarminConstants";
import { setGarminConnectInstalled } from "../../redux/actions/garminActions";
import { updateSelectedDevice } from "./GarminWatchHelper";
import { App } from "@capacitor/app";

export class GarminWatch implements WatchInterface {
    _stream: Observable<any>;
    messageObservers: Observer<any>[] = [];
    previousState?: GarminWatches;
    listener: PluginListenerHandle | undefined;
    statusListener: PluginListenerHandle | undefined;
    public name = "GarminWatch";
    public watchType = WatchType.garmin;

    constructor() {
        if (!Capacitor.isNativePlatform()) {
            console.log(
                "[GarminWatch] Not running on native platform, skipping initialisation"
            );
            this._stream = new Observable<any>(() => {
                // Do nothing
            });
            return;
        }

        console.log("[GarminWatch] init");

        GarminSDK.checkGarminConnectInstalled().then(async (result) => {
            if (result.installed) {
                store.dispatch(setGarminConnectInstalled(true));
                console.log("[GarminWatch] Garmin Connect is installed");
                await GarminSDK.initialiseGarminSdk();
                console.log("[GarminWatch] Garmin SDK initialised");
            } else {
                store.dispatch(setGarminConnectInstalled(false));
                console.log(
                    "[GarminWatch] Garmin Connect is not installed - skipping initialisation"
                );
            }
        });

        this._stream = new Observable<any>((messageObserver) => {
            this.messageObservers.push(messageObserver);
        });

        // Start listening for a selected device change
        store.subscribe(async () => {
            const newState = store.getState().garminWatches;

            // if the selected device has been removed, clean up the listeners and state
            if (
                this.previousState &&
                this.previousState.selectedDevice &&
                !newState?.selectedDevice
            ) {
                console.log(
                    "[GarminWatch] Selected device removed - cleaning up listeners"
                );
                const deviceToCleanup = this.previousState.selectedDevice;
                this.previousState = newState;  // ← Update IMMEDIATELY
                this.removeMessageListener(deviceToCleanup).catch((e) => {
                    console.error(
                        "[GarminWatch] Error removing message listener",
                        e
                    );
                });
                this.removeStatusListener(deviceToCleanup).catch((e) => {
                    console.error(
                        "[GarminWatch] Error removing status listener",
                        e
                    );
                });
            }

            if (
                newState &&
                newState.selectedDevice &&
                newState.selectedDevice.uuid !==
                    this.previousState?.selectedDevice?.uuid
            ) {
                console.log(
                    "[GarminWatch] Selected device changed",
                    newState.selectedDevice.friendlyName
                );

                // ← Update previousState IMMEDIATELY to prevent re-entry
                const deviceToSetup = newState.selectedDevice;
                this.previousState = newState;

                try {
                    // Wait for SDK initialization to complete
                    await GarminSDK.initialiseGarminSdk();
                    console.log("[GarminWatch] SDK initialised");

                    console.log("[GarminWatch] Updating selected device status");
                    // try and get the latest status to replace any old status in
                    // redux. The listeners also do not fire the current status when
                    // they are setup
                    updateSelectedDevice(deviceToSetup).catch((e) => {
                        console.log("[GarminWatch] Error updating selected device on startup", e);
                    });

                    await this.setupListener(deviceToSetup);

                    this.listenToDeviceStatus(deviceToSetup).catch((e) => {
                        console.error("[GarminWatch] Error setting up status listener", e);
                    });

                    App.addListener("appStateChange", ({ isActive }) => {
                        const selectedDevice =
                            store.getState().garminWatches?.selectedDevice;
                        // on app resume, check the status again
                        if (isActive && selectedDevice) {
                            console.log("Attempting to update status");
                            // check the connection and app status
                            updateSelectedDevice(selectedDevice).catch((e) => {
                                console.error(
                                    "Could not update selected device",
                                    e
                                );
                            });
                        }
                    });
                } catch (e) {
                    console.error(
                        "[GarminWatch] Error during Garmin setup",
                        e
                    );
                }
            }

            this.previousState = newState;
        });
    }

    private async removeStatusListener(device: IQDevice) {
        if (this.statusListener) {
            try {
                await GarminSDK.unregisterForDeviceEvents({ device });
                await this.statusListener.remove();
            } catch (e) {
                console.error(
                    "[GarminWatch] Error unregistering for device events",
                    e
                );
            }

            this.statusListener = undefined;
        }
    }

    private listenToDeviceStatus = async (device: IQDevice) => {
        await this.removeStatusListener(device);

        await GarminSDK.registerForDeviceEvents({ device });

        this.statusListener = await GarminSDK.addListener(
            "deviceStatusChanged",
            this.deviceStatusListener
        );
    };

    private deviceStatusListener = (
        statusInfo: GarminDeviceStatusChangeEvent
    ) => {
        const { uuid, status, isConnected } = statusInfo;

        if (uuid !== store.getState().garminWatches?.selectedDevice?.uuid) {
            console.log(
                `[GarminStatus] Ignoring status change for device ${uuid} as it is not the selected device ${
                    store.getState().garminWatches?.selectedDevice?.uuid
                }`
            );
            return;
        }

        const garminWatches = store.getState().garminWatches;
        const selectedDevice = garminWatches?.selectedDevice;

        if (!selectedDevice) {
            console.error("[GarminStatus] Selected device is undefined");
            return;
        }

        const updatedDevice = {
            ...selectedDevice,
            status,
            isConnected,
        };

        updateSelectedDevice(updatedDevice).catch((e) => {
            console.error("Could not update selected device", e);
        });

        console.log(
            `[GarminStatus] Device status changed for device ${uuid}: ${status}`
        );
    };

    private async removeMessageListener(selectedDevice: IQDevice) {
        if (this.listener) {
            try {
                await GarminSDK.stopListeningToDevice({
                    device: selectedDevice,
                    app: GARMIN_FULL_APP_INFO,
                });
                await this.listener.remove();
            } catch (e) {
                console.error(
                    "[GarminWatch] Error stopping message listener",
                    e
                );
            }

            this.listener = undefined;
        }
    }

    private async setupListener(selectedDevice: IQDevice) {
        // clean up the old listener
        await this.removeMessageListener(selectedDevice);

        // start listening to the newly selected device
        this.listener = await GarminSDK.addListener(
            "messageReceived",
            this.messageReceived.bind(this)
        );
        GarminSDK.startListeningToDevice({
            device: selectedDevice, // Pass the existing selectedDevice object
            app: GARMIN_FULL_APP_INFO,
        });
        console.log(
            `[GarminWatch] listener setup for ${selectedDevice.friendlyName}`
        );
    }

    private messageReceived(data: any) {
        console.log(`[GarminWatch] message received ${data.messageId}`);
        if (!this.messageObservers.length) {
            return Sentry.captureMessage(
                "[GarminWatch] Received message but observer is undefined"
            );
        }
        this.messageObservers.forEach((observer) => {
            observer.next(data);
        });
    }

    messageReceivedStream(): Observable<any> {
        return this._stream;
    }

    sendMessage(payload: any): Promise<any> {
        console.log("[GarminWatch] Sending message", payload);
        payload = copyObject(payload);
        // mutates existing obj to remove nulls that can crash apple watch
        deleteNullProperties(payload, true);
        const currentDevice = store.getState().garminWatches?.selectedDevice;
        if (!currentDevice) {
            return Promise.reject("No Garmin device selected");
        }
        return GarminSDK.sendMessageToWatch({
            device: currentDevice,
            app: GARMIN_FULL_APP_INFO,
            payload,
        });
    }
}
